import React, { useState, useEffect } from 'react';
import { Bar, Doughnut, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement
} from 'chart.js';
import DateRangeFilter from '../components/DateRangeFilter';
import LoadingOverlay from '../components/LoadingOverlay';
import { fetchPublishedBooksPerDay, fetchBooksPerReadingLevel, fetchBooksPerLanguage, fetchBooksPerBookType, fetchBooksPerAgeRating } from '../services/api';
import { getLevelColors, getLanguageColors } from '../utils/chartColors';
import { groupByWeek, groupByMonth, fillDateGaps } from '../utils/dataGrouping';
import { formatCompactNumber, formatAgeRating } from '../utils/formatters';
import './DataPages.css';
import './Pages.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const BooksOverview = ({ isDarkMode }) => {
  const toUTCDateStr = (date) => `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;

  const getDefaultRange = () => {
    const end = new Date();
    const start = new Date();
    start.setUTCDate(start.getUTCDate() - 29);
    return {
      startDate: toUTCDateStr(start),
      endDate: toUTCDateStr(end)
    };
  };

  const [startDate, setStartDate] = useState(getDefaultRange().startDate);
  const [endDate, setEndDate] = useState(getDefaultRange().endDate);
  const [data, setData] = useState([]);
  const [readingLevelData, setReadingLevelData] = useState([]);
  const [languageData, setLanguageData] = useState([]);
  const [bookTypeData, setBookTypeData] = useState([]);
  const [ageRatingData, setAgeRatingData] = useState([]);
  const [earliestDate, setEarliestDate] = useState(null);
  const [latestDate, setLatestDate] = useState(null);
  const [publishedView, setPublishedView] = useState('daily');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadData = async (start, end, isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      const [booksPerDayResult, booksPerReadingLevel, booksPerLanguage, booksPerBookType, booksPerAgeRating] = await Promise.all([
        fetchPublishedBooksPerDay(start, end),
        fetchBooksPerReadingLevel(start, end),
        fetchBooksPerLanguage(start, end),
        fetchBooksPerBookType(start, end),
        fetchBooksPerAgeRating(start, end)
      ]);
      setData(fillDateGaps(booksPerDayResult.items || []));
      if (booksPerDayResult.earliestDate) setEarliestDate(booksPerDayResult.earliestDate);
      if (booksPerDayResult.latestDate) setLatestDate(booksPerDayResult.latestDate);
      setReadingLevelData(booksPerReadingLevel);
      setLanguageData(booksPerLanguage);
      setBookTypeData(booksPerBookType);
      setAgeRatingData(booksPerAgeRating);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    const { startDate: start, endDate: end } = getDefaultRange();
    loadData(start, end, false);
  }, []);

  const handleApply = (directStartDate = null, directEndDate = null) => {
    const fetchStartDate = directStartDate ?? startDate;
    const fetchEndDate = directEndDate ?? endDate;
    loadData(fetchStartDate, fetchEndDate, true);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const textColor = isDarkMode ? '#e5e7eb' : '#333333';
  const totalBooks = data.reduce((sum, item) => sum + item.count, 0);

  const getGroupedData = () => {
    if (publishedView === 'weekly') return groupByWeek(data);
    if (publishedView === 'monthly') return groupByMonth(data);
    return data;
  };
  const groupedData = getGroupedData();

  const getPeriodLabel = () => {
    if (!startDate || !endDate) return 'All Time';
    const s = new Date(startDate);
    const e = new Date(endDate);
    const days = Math.ceil(Math.abs(e - s) / (1000 * 60 * 60 * 24)) + 1;
    return `${days} Days`;
  };

  const chartData = {
    labels: groupedData.map(item => item.label || formatDate(item.date)),
    datasets: [{
      label: 'Published Books',
      data: groupedData.map(item => item.count),
      backgroundColor: 'rgba(175, 62, 255, 0.8)',
      borderColor: '#af3eff',
      borderWidth: 2,
      borderRadius: 8,
      hoverBackgroundColor: '#af3eff',
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(51, 51, 51, 0.95)',
        padding: 12,
        titleColor: '#fff',
        bodyColor: '#fff',
        displayColors: false,
        callbacks: {
          title: (tooltipItems) => {
            const label = tooltipItems[0].label;
            if (/^\d{4}-\d{2}-\d{2}$/.test(label)) {
              const dayName = new Date(label + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' });
              return [label, dayName];
            }
            return label;
          },
          label: (context) => `Books: ${context.parsed.y}`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { color: textColor, font: { size: 12 } },
        grid: { color: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }
      },
      x: {
        ticks: { color: textColor, font: { size: 11 }, maxRotation: 45 },
        grid: { display: false }
      }
    },
    animation: {
      duration: 1500,
      easing: 'easeInOutQuart'
    }
  };

  // Reading Level - Doughnut Chart
  const readingLevelChartData = {
    labels: readingLevelData.map(item => `Level ${item.level}` || 'Unknown'),
    datasets: [{
      data: readingLevelData.map(item => item.count),
      backgroundColor: getLevelColors(readingLevelData.map(item => item.level), isDarkMode),
      borderColor: isDarkMode ? '#1f2937' : '#ffffff',
      borderWidth: 2,
      hoverOffset: 8
    }]
  };

  const readingLevelChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '60%',
    plugins: {
      legend: {
        position: isMobile ? 'bottom' : 'right',
        labels: {
          color: textColor,
          padding: isMobile ? 12 : 16,
          font: { size: isMobile ? 11 : 12 },
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(51, 51, 51, 0.95)',
        padding: 12,
        titleColor: '#fff',
        bodyColor: '#fff',
        displayColors: false,
        callbacks: {
          label: (context) => {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return `Books: ${context.parsed.toLocaleString()} (${percentage}%)`;
          }
        }
      }
    },
    animation: {
      duration: 1500,
      easing: 'easeInOutQuart'
    }
  };

  // Language - Pie Chart
  const languageLabels = languageData.map(item => item.languageCode || item.language || 'Unknown');
  const languageChartData = {
    labels: languageLabels,
    datasets: [{
      data: languageData.map(item => item.count),
      backgroundColor: getLanguageColors(languageLabels, isDarkMode),
      borderColor: isDarkMode ? '#1f2937' : '#ffffff',
      borderWidth: 3,
      hoverBorderWidth: 4,
      hoverOffset: 15
    }]
  };

  const languageChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: isMobile ? 'bottom' : 'right',
        labels: {
          color: textColor,
          padding: isMobile ? 12 : 16,
          font: { size: isMobile ? 11 : 12, family: "'Inter', sans-serif" },
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(51, 51, 51, 0.95)',
        padding: 12,
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#af3eff',
        borderWidth: 1,
        displayColors: false,
        callbacks: {
          label: (context) => {
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `Books: ${value.toLocaleString()} (${percentage}%)`;
          }
        }
      }
    },
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 1500,
      easing: 'easeInOutQuart'
    }
  };

  // Book Type - Doughnut Chart (reusing level/language palette)
  const bookTypeChartColors = isDarkMode ? [
    'rgba(192, 132, 252, 0.9)',   // Purple
    'rgba(96, 165, 250, 0.9)',    // Blue
    'rgba(52, 211, 153, 0.9)',    // Green
    'rgba(248, 113, 113, 0.9)',   // Red
    'rgba(34, 211, 238, 0.9)',    // Cyan
    'rgba(244, 114, 182, 0.9)',   // Pink
    'rgba(250, 204, 21, 0.9)',    // Yellow
  ] : [
    'rgba(147, 51, 234, 0.8)',    // Purple
    'rgba(37, 99, 235, 0.8)',     // Blue
    'rgba(5, 150, 105, 0.8)',     // Green
    'rgba(220, 38, 38, 0.8)',     // Red
    'rgba(6, 182, 212, 0.8)',     // Cyan
    'rgba(219, 39, 119, 0.8)',    // Pink
    'rgba(202, 138, 4, 0.8)',     // Yellow
  ];
  const bookTypeChartData = {
    labels: bookTypeData.map(item => item.bookType || 'Unknown'),
    datasets: [{
      data: bookTypeData.map(item => item.count),
      backgroundColor: bookTypeData.map((_, i) => bookTypeChartColors[i % bookTypeChartColors.length]),
      borderColor: isDarkMode ? '#1f2937' : '#ffffff',
      borderWidth: 2,
      hoverOffset: 8
    }]
  };

  const bookTypeDoughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '60%',
    plugins: {
      legend: {
        position: isMobile ? 'bottom' : 'right',
        labels: {
          color: textColor,
          padding: isMobile ? 12 : 16,
          font: { size: isMobile ? 11 : 12 },
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(51, 51, 51, 0.95)',
        padding: 12,
        titleColor: '#fff',
        bodyColor: '#fff',
        displayColors: false,
        callbacks: {
          label: (context) => {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return `Books: ${context.parsed.toLocaleString()} (${percentage}%)`;
          }
        }
      }
    },
    animation: { duration: 1500, easing: 'easeInOutQuart' }
  };

  // Age Rating - Doughnut Chart (reusing level/language palette)
  const ageRatingChartColors = isDarkMode ? [
    'rgba(52, 211, 153, 0.9)',    // Green
    'rgba(96, 165, 250, 0.9)',    // Blue
    'rgba(99, 102, 241, 0.9)',    // Indigo
    'rgba(251, 146, 60, 0.9)',    // Orange
    'rgba(248, 113, 113, 0.9)',   // Red
    'rgba(163, 230, 53, 0.9)',    // Lime
  ] : [
    'rgba(5, 150, 105, 0.8)',     // Green
    'rgba(37, 99, 235, 0.8)',     // Blue
    'rgba(67, 56, 202, 0.8)',     // Indigo
    'rgba(234, 88, 12, 0.8)',     // Orange
    'rgba(220, 38, 38, 0.8)',     // Red
    'rgba(101, 163, 13, 0.8)',    // Lime
  ];
  const ageRatingChartData = {
    labels: ageRatingData.map(item => formatAgeRating(item.ageRating) || 'Unknown'),
    datasets: [{
      data: ageRatingData.map(item => item.count),
      backgroundColor: ageRatingData.map((_, i) => ageRatingChartColors[i % ageRatingChartColors.length]),
      borderColor: isDarkMode ? '#1f2937' : '#ffffff',
      borderWidth: 2,
      hoverOffset: 8
    }]
  };

  const ageRatingDoughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '60%',
    plugins: {
      legend: {
        position: isMobile ? 'bottom' : 'right',
        labels: {
          color: textColor,
          padding: isMobile ? 12 : 16,
          font: { size: isMobile ? 11 : 12 },
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(51, 51, 51, 0.95)',
        padding: 12,
        titleColor: '#fff',
        bodyColor: '#fff',
        displayColors: false,
        callbacks: {
          label: (context) => {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return `Books: ${context.parsed.toLocaleString()} (${percentage}%)`;
          }
        }
      }
    },
    animation: { duration: 1500, easing: 'easeInOutQuart' }
  };

  const totalReadingLevelBooks = readingLevelData.reduce((sum, item) => sum + item.count, 0);
  const totalLanguageBooks = languageData.reduce((sum, item) => sum + item.count, 0);
  const totalBookTypeBooks = bookTypeData.reduce((sum, item) => sum + item.count, 0);
  const totalAgeRatingBooks = ageRatingData.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="page-container">
      {refreshing && <LoadingOverlay />}

      <div className="page-header">
        <div className="page-header-content">
          <h1>Books Overview</h1>
          <div className="page-header-badges">
            <div className="control-badge">
              <span className="badge-label">Period</span>
              <span className="badge-value">{getPeriodLabel()}</span>
            </div>
            {!loading && (
              <div className="control-badge">
                <span className="badge-label">Published</span>
                <span className="badge-value">{totalBooks.toLocaleString()}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="data-page-content">
        <div className="filters-container">
          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onApply={handleApply}
            earliestDate={earliestDate}
            latestDate={latestDate}
            showMax={true}
          />
        </div>

        {error && <div className="table-empty">Error: {error}</div>}
        {loading && <div className="table-loading">Loading ...</div>}

        {!loading && !error && data.length > 0 && (
          <div className="data-page-chart-card">
            <div className="chart-header">
              <h3>Published Books</h3>
              <span className="chart-badge">
                {formatCompactNumber(totalBooks)} books
              </span>
            </div>
            <div className="chart-wrapper">
              <Bar data={chartData} options={chartOptions} />
            </div>
          </div>
        )}

        {!loading && !error && (
          <div className="charts-grid">
            {readingLevelData.length > 0 && (
              <div className="data-page-chart-card">
                <div className="chart-header">
                  <h3>Published Books by Reading Level</h3>
                  <span className="chart-badge">
                    {formatCompactNumber(totalReadingLevelBooks)} books
                  </span>
                </div>
                <div className="chart-wrapper chart-wrapper-tall">
                  <Doughnut data={readingLevelChartData} options={readingLevelChartOptions} />
                </div>
              </div>
            )}

            {languageData.length > 0 && (
              <div className="data-page-chart-card">
                <div className="chart-header">
                  <h3>Published Books by Language</h3>
                  <span className="chart-badge">
                    {formatCompactNumber(totalLanguageBooks)} books
                  </span>
                </div>
                <div className="chart-wrapper chart-wrapper-tall">
                  <Pie data={languageChartData} options={languageChartOptions} />
                </div>
              </div>
            )}

            {bookTypeData.length > 0 && (
              <div className="data-page-chart-card">
                <div className="chart-header">
                  <h3>Published Books by Book Type</h3>
                  <span className="chart-badge">
                    {formatCompactNumber(totalBookTypeBooks)} books
                  </span>
                </div>
                <div className="chart-wrapper chart-wrapper-tall">
                  <Doughnut data={bookTypeChartData} options={bookTypeDoughnutOptions} />
                </div>
              </div>
            )}

            {ageRatingData.length > 0 && (
              <div className="data-page-chart-card">
                <div className="chart-header">
                  <h3>Published Books by Age Rating</h3>
                  <span className="chart-badge">
                    {formatCompactNumber(totalAgeRatingBooks)} books
                  </span>
                </div>
                <div className="chart-wrapper chart-wrapper-tall">
                  <Doughnut data={ageRatingChartData} options={ageRatingDoughnutOptions} />
                </div>
              </div>
            )}
          </div>
        )}

        {!loading && !error && data.length === 0 && (
          <div className="table-empty">
            No published books data for the selected period.
          </div>
        )}
      </div>
    </div>
  );
};

export default BooksOverview;
